import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import axios from "axios";
import { GraduationCap, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import PublicHeader from "@/components/PublicHeader";
import ScholarAvatar from "@/components/ScholarAvatar";
import VerifiedBadge from "@/components/VerifiedBadge";
import Seo from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Edu = ({ label, college, year }) => college ? (
  <li className="flex items-start gap-2 text-sm"><GraduationCap className="w-4 h-4 mt-0.5 text-[color:var(--ayur-terracotta)]" />
    <span><b>{label}</b> · {college}{year ? ` · Batch ${year}` : ""}</span></li>
) : null;

export default function PublicScholar() {
  const { id } = useParams();
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { axios.get(`${API}/public/scholars/${id}`).then((r) => setS(r.data)).catch(() => setErr(true)); }, [id]);
  if (user) return <Navigate to={user.id === id ? "/app/profile" : `/app/scholars/${id}`} replace />;

  const desc = s && `${s.full_name} — ${s.degree || "Ayurveda scholar"}${s.specialty ? `, ${s.specialty}` : ""}${s.college ? `, ${s.college}` : ""}. Connect on VaidGram.`;
  return (
    <div className="min-h-screen bg-[color:var(--ayur-cream)]" data-testid="public-scholar-page">
      {s && <Seo title={`${s.full_name} · ${s.degree || "Ayurveda scholar"}`} description={desc} path={`/scholars/${id}`} type="profile"
        jsonLd={{ "@context": "https://schema.org", "@type": "ProfilePage", mainEntity: { "@type": "Person", name: s.full_name, jobTitle: s.specialty || s.degree, alumniOf: s.college } }} />}
      {err && <Seo title="Scholar not found" path={`/scholars/${id}`} noindex />}
      <PublicHeader />
      <main className="max-w-3xl mx-auto px-6 py-10">
        {err && <div className="paper-card p-10 text-center" data-testid="public-scholar-notfound">Scholar not found.</div>}
        {!s && !err && <div className="paper-card p-10 text-center text-[color:var(--ayur-mute)]">Loading…</div>}
        {s && (
          <article className="paper-card bg-white p-6 sm:p-8" data-testid="public-scholar-card">
            <div className="flex items-center gap-4">
              <ScholarAvatar user={s} size={80} />
              <div>
                <h1 className="font-serif-display text-3xl text-[color:var(--ayur-emerald)] inline-flex items-center gap-2" data-testid="public-scholar-name">
                  {s.full_name}{s.verified && <VerifiedBadge size={20} />}
                </h1>
                <div className="text-sm text-[color:var(--ayur-ink-2)]">{s.degree}{s.specialty ? ` · ${s.specialty}` : ""}{s.is_guru ? " · Guru" : ""}</div>
                {s.location && <div className="text-xs text-[color:var(--ayur-mute)] inline-flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" />{s.location}</div>}
              </div>
            </div>
            {s.bio && <p className="mt-5 leading-relaxed text-[color:var(--ayur-ink)]">{s.bio}</p>}
            <ul className="mt-5 space-y-2" data-testid="public-scholar-education">
              <Edu label="BAMS" college={s.college} year={s.batch_year} />
              <Edu label="MD" college={s.md_college} year={s.md_batch_year} />
              <Edu label="MS" college={s.ms_college} year={s.ms_batch_year} />
              <Edu label="PhD" college={s.phd_college} year={s.phd_batch_year} />
            </ul>
            <div className="mt-5 flex gap-5 text-sm text-[color:var(--ayur-ink-2)]" data-testid="public-scholar-stats">
              <span><b>{s.followers_count}</b> followers</span><span><b>{s.following_count}</b> following</span><span><b>{s.post_count}</b> posts</span>
            </div>
            <div className="mt-8 p-5 rounded-xl bg-[color:var(--ayur-emerald)] text-white flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <span className="text-sm">Join VaidGram to follow {s.full_name.split(" ")[0]} and read their posts.</span>
              <Link to="/signup"><Button className="rounded-full bg-[color:var(--ayur-gold)] text-[color:var(--ayur-ink)] hover:bg-white" data-testid="public-scholar-join-btn">Join to follow</Button></Link>
            </div>
          </article>
        )}
      </main>
    </div>
  );
}
